import { createTranslator, type useTranslations } from "next-intl"
import english from "@/translations/en/quality-counts.json"
import type { KpiCardProps } from "@/components/ui/analytics/kpi-card"
import { computeShareTrend, type MetricKind } from "@/lib/analytics/deltas"
import { MIN_SHARE_POPULATION, NO_VALUE, type ShareCounts } from "@/lib/conversation-quality/metric-definitions"
import { CHANNEL_LABELS } from "@/lib/agents/messaging/types"
import type { ConversationReference } from "@/lib/conversation-quality/sources/types"

type LinkedReference = ConversationReference & {sessionNumber: number | null}
const englishTranslator = createTranslator({locale: "en", messages: english, namespace: "QualityCounts"})

export const { shareComparison, duration, sessionCount, requestTotal, requestsOf, formatWindow, referenceLabel, channelLabel } = getQualitySectionFormatters()

export function getQualitySectionFormatters(t: ReturnType<typeof useTranslations<"QualityCounts">> = englishTranslator, locale = "en") {
  function shareComparison(
    current: number | null,
    previous: ShareCounts | null,
    kind: MetricKind
  ): Pick<KpiCardProps, "delta" | "baseline"> {
    if (current === null || previous === null) return {}
    if (previous.over < MIN_SHARE_POPULATION) return { baseline: t("tooFewSessions") }
    return { delta: computeShareTrend(current, previous.of / previous.over, kind) }
  }

  function duration(ms: number | null): string {
    if (ms === null) return NO_VALUE
    if (ms < 1_000) return t("lessThanSecond")
    if (ms < 59_500) return t("seconds", {amount: Math.round(ms / 1_000)})
    const hours = ms / 3_600_000
    if (hours < 1) return t("minutes", {amount: Math.round(ms / 60_000)})
    const number = new Intl.NumberFormat(locale, {minimumFractionDigits: 1, maximumFractionDigits: 1})
    if (hours < 48) return t("hours", {amount: number.format(hours)})
    return t("days", {amount: number.format(hours / 24)})
  }

  function sessionCount(sessions: number): string {
    return t("sessions", {count: sessions})
  }

  function requestTotal(requests: number): string {
    return t("requests", {count: requests})
  }

  function requestsOf({ of, over }: ShareCounts): string {
    return t("requestsOf", {count: of, total: requestTotal(over)})
  }

  function formatWindow(range: { start: string; end: string }, timezone: string): string {
    const format = new Intl.DateTimeFormat("en", { month: "short", day: "numeric", timeZone: timezone })
    const part = (at: number, type: "month" | "day") =>
      format.formatToParts(new Date(at)).find((p) => p.type === type)?.value ?? ""
    const start = Date.parse(range.start)
    const last = Date.parse(range.end) - 1
    if (locale !== "en") return new Intl.DateTimeFormat(locale, {month: "short", day: "numeric", timeZone: timezone}).formatRange(start, last)

    if (part(start, "month") === part(last, "month")) {
      const days = part(start, "day") === part(last, "day") ? part(start, "day") : `${part(start, "day")} – ${part(last, "day")}`
      return `${part(start, "month")} ${days}`
    }
    return `${part(start, "month")} ${part(start, "day")} – ${part(last, "month")} ${part(last, "day")}`
  }

  function referenceLabel({ contactTitle, sessionNumber }: LinkedReference): string {
    return sessionNumber === null ? contactTitle : t("reference", {name: contactTitle, number: sessionNumber})
  }

  function channelLabel(channel: string): string {
    switch (channel) {
      case "web_widget": return t("webWidget")
      case "web_demo": return t("webDemo")
      default: return CHANNEL_LABELS[channel as keyof typeof CHANNEL_LABELS] ?? channel
    }
  }

  return { shareComparison, duration, sessionCount, requestTotal, requestsOf, formatWindow, referenceLabel, channelLabel }
}
